<script setup lang="ts">
import { computed, ref } from 'vue';
import ChevronDownIcon from 'src/shared/icons/chevron-down.svg';
import CheckIcon from 'src/shared/icons/check.svg';
import SwapVertIcon from 'src/shared/icons/swap-vert-icon.svg';
import { useCenteredDashedUnderline } from 'src/shared/utils/useCenteredDashedUnderline';

export interface SortingDropdownOption {
  value: string;
  label: string;
}

export interface SortingDropdownProps {
  options: SortingDropdownOption[];
  showIcon?: boolean;
}

const props = withDefaults(defineProps<SortingDropdownProps>(), {
  showIcon: true,
});

const model = defineModel<string>({ required: true });

const isActive = ref(false);
const sortingLabelRef = ref<HTMLElement | null>(null);
const { width: underlineWidth, segments: underlineSegments } =
  useCenteredDashedUnderline(sortingLabelRef);

const label = computed(
  () => props.options.find((option) => option.value === model.value)?.label ?? '',
);

function toggleDropdown(): void {
  isActive.value = !isActive.value;
}

function pick(value: string): void {
  model.value = value;
  isActive.value = false;
}
</script>

<template>
  <div class="dropdown" :class="{ 'dropdown--no-icon': !showIcon }">
    <div class="sorting-btn" @click="toggleDropdown()">
      <SwapVertIcon v-if="showIcon" class="swap-icon" />
      <span ref="sortingLabelRef" class="sorting-btn-text">
        <typography :variant="'body-3'">{{ label }}</typography>
        <svg
          v-if="underlineWidth"
          class="sorting-btn-underline"
          aria-hidden="true"
          :width="underlineWidth"
          height="1"
        >
          <rect
            v-for="(segment, index) in underlineSegments"
            :key="index"
            :x="segment.x"
            y="0"
            :width="segment.width"
            height="1"
            fill="currentColor"
          />
        </svg>
      </span>
      <ChevronDownIcon class="chevron-icon" :class="[{ 'to-up': isActive }]" />
    </div>
    <div v-if="isActive" class="dropdown-content">
      <typography
        v-for="option in options"
        :key="option.value"
        :variant="'body-3'"
        class="dropdown-content-item"
        @click="pick(option.value)"
      >
        <span class="check-icon">
          <CheckIcon v-if="model === option.value" />
        </span>
        {{ option.label }}
      </typography>
    </div>
  </div>
</template>

<style scoped>
.dropdown {
  z-index: 2;
  position: relative;

  .sorting-btn {
    display: flex;
    align-items: center;
    gap: var(--spacing-s);
    color: var(--palette-blue-light);
    border-radius: var(--spacing-l);
    cursor: pointer;

    .swap-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      color: var(--palette-black);
    }

    .sorting-btn-text {
      position: relative;
      display: inline-block;
      line-height: 24px;
      padding-bottom: 2px;
      user-select: none;
    }

    .sorting-btn-underline {
      position: absolute;
      left: 0;
      bottom: 0;
      display: block;
      color: currentColor;
      pointer-events: none;
    }

    .chevron-icon {
      height: 16px;
      transition: all 250ms ease-in-out;

      &.to-up {
        transform: rotateZ(180deg);
      }
    }

    &:hover {
      color: var(--palette-blue);

      .swap-icon {
        color: var(--palette-blue);
      }
    }
  }

  &.dropdown--no-icon {
    .sorting-btn {
      height: 24px;
      box-sizing: border-box;
    }

    .sorting-btn-text {
      line-height: 20px;
    }
  }

  .dropdown-content {
    position: absolute;
    top: 26px;
    right: 0;
    border-radius: var(--spacing-l);
    background-color: var(--palette-white);
    padding: var(--spacing-s);
    width: auto;
    box-shadow: 0 16px 32px 0 #00000014;

    .check-icon {
      width: 16px;
      margin-right: var(--spacing-l);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dropdown-content-item {
      height: 44px;
      width: 100%;
      min-width: max-content;
      display: flex;
      justify-content: center;
      padding: 0 var(--spacing-xl);
      align-items: center;
      cursor: pointer;
      border-radius: var(--spacing-m);

      &:hover {
        background-color: var(--palette-gray-100);
      }
    }
  }
}

@media (width <= 755px) {
  .dropdown {
    width: 100%;
    min-width: 0;
  }

  .dropdown.dropdown--no-icon .sorting-btn {
    width: 100%;
    height: auto;
    min-height: 44px;
    justify-content: space-between;
    padding: 10px 12px;
    background: var(--palette-white);
    border-radius: var(--border-radius-l);
  }

  .dropdown.dropdown--no-icon .sorting-btn-text {
    flex: 1;
    min-width: 0;
    line-height: 20px;
  }

  .sorting-btn-text :deep(p) {
    white-space: normal;
  }

  .chevron-icon {
    width: 14px;
    flex: 0 0 14px;
  }

  .dropdown .dropdown-content {
    right: auto;
    left: 0;
    width: 100%;
  }

  .dropdown .dropdown-content .dropdown-content-item {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: 44px;
    height: auto;
    justify-content: flex-start;
    padding: 10px 8px;
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    white-space: normal;
  }

  .dropdown .dropdown-content .check-icon {
    margin-right: var(--spacing-s);
  }
}
</style>
